import React, { useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { EKycStep } from "@adgm/cross-models";
import type { TInstitutionInformationForm, TKycFormV2 } from "@adgm/cross-models";
import type { IGenericFormRef } from "./GenericForm";
import GenericForm from "./GenericForm";
import { INSTITUTION_INFORMATION_CONFIG } from "./configs";
import { transformData } from "@adgm/libs";
import { cloneDeep } from "lodash-es";
import { INSTITUTION_INFORMATION_FORM } from "@adgm/cross-config";

interface IProps {
  kycForm: TKycFormV2;
  updateKycForm: (_form: Partial<TKycFormV2>) => void;
}

/**
 * KycInformationForm 组件的 Ref 接口
 */
export interface IKycInformationFormRef {
  /** 校验表单数据 */
  checkData: () => { hasError: boolean; errMsg?: string };
}

/**
 * KYC 机构信息表单组件
 * 这是一个专门用于机构 KYC 流程中信息收集部分的表单组件。
 * 用于收集机构的基本信息，如公司名称、注册地址、业务类型等。
 *
 * 主要功能：
 * - 机构基本信息的收集和验证
 * - 表单数据的自动初始化和恢复
 * - 实时数据验证和错误提示
 * - 与 KYC 流程的集成
 * - 支持表单数据的保存和提交
 */
export const KycInformationForm = forwardRef<IKycInformationFormRef, IProps>((props, ref) => {
  const { kycForm, updateKycForm } = props;

  /**
   * 初始化机构信息表单数据
   * 从配置中获取机构信息表单的初始数据，并应用现有的 KYC 表单数据
   */
  const institutionForm = useMemo(() => {
    const institutionForm: TInstitutionInformationForm = cloneDeep(INSTITUTION_INFORMATION_FORM);
    transformData(institutionForm, kycForm.institution);
    return institutionForm;
  }, []); //只执行一次,不要添加依赖
  const formRef = useRef<IGenericFormRef<TInstitutionInformationForm>>(null);

  /**
   * 处理表单数据变化
   * 当表单数据发生变化时，更新 KYC 表单并设置步骤为验证阶段
   * @param formData 更新后的机构信息表单数据
   */
  const handleFormChange = useCallback(
    (formData: TInstitutionInformationForm) => {
      updateKycForm({
        institution: { ...kycForm.institution, ...formData },
        institutionStep: EKycStep.VERIFICATION,
      });
    },
    [updateKycForm, kycForm],
  );

  /**
   * 校验表单数据
   * 验证表单中的所有字段，返回验证结果
   * @returns 验证结果，包含错误状态和错误信息
   */
  const checkData = useCallback((): { hasError: boolean } => {
    if (formRef.current) {
      return formRef.current.checkData();
    }
    return { hasError: false };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      checkData,
    }),
    [checkData],
  );

  /** 表单配置 */
  const formConfig = useMemo(
    () => ({
      config: INSTITUTION_INFORMATION_CONFIG,
      initialData: institutionForm,
    }),
    [institutionForm],
  );

  return (
    <GenericForm
      {...formConfig}
      genericFormType="information"
      onChange={handleFormChange}
      ref={formRef}
    />
  );
});
